import React, { useState, useEffect } from 'react';
import { Search, Grid3X3, ChevronDown, Star, ArrowLeft, MapPin, Briefcase, Users, Building2 } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter';
import baseurl from '../Baseurl/baseurl';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Heading, Eyebrow, Avatar, Loader, EmptyState, Container } from '../ui';

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" fill="#25D366" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor" />
  </svg>
);

const BusinessCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showBusinesses, setShowBusinesses] = useState(false);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
  const [categoryItemsPerPage] = useState(9);
  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();

  // Single green gradient color scheme for all categories
  const greenColorScheme = {
    bgColor: "bg-surface2",
    headerBg: "bg-primary",
    iconBg: "bg-primary",
    buttonColor: "bg-primary hover: ",
    textColor: "text-primary",
    countBg: "bg-primary/10 text-primary"
  };

  // Helpers to resolve media URLs safely (matching HomePage)
  const toAbsoluteUrl = (path) => {
    if (!path) return '';
    const trimmed = String(path).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `${baseurl}/${trimmed.replace(/^\//, '')}`;
  };

  // Get banner image URL (matching HomePage logic)
  const getBannerImageUrl = (business) => {
    // Prefer explicit business profile image
    if (business?.business_profile_image) {
      return toAbsoluteUrl(business.business_profile_image);
    }

    // Fallback: first image (not video) from media_gallery
    const gallery = (business?.media_gallery || '').split(',').map((s) => s.trim()).filter(Boolean);
    const imageEntry = gallery.find((entry) => /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(entry));
    if (imageEntry) return toAbsoluteUrl(imageEntry);

    return '';
  };

  // Function to calculate average rating for a business (EXACTLY MATCHING HOMEPAGE)
  const getBusinessRatings = (businessId) => {
    const businessRatings = ratings.filter(rating => {
      const rBid = parseInt(rating.business_id, 10);
      const bId = parseInt(businessId, 10);
      return !Number.isNaN(rBid) && !Number.isNaN(bId) && rBid === bId;
    });

    if (businessRatings.length === 0) {
      return { averageRating: 0, reviewCount: 0 };
    }

    const totalRating = businessRatings.reduce((sum, rating) => sum + Number(rating.rating || 0), 0);
    const averageRating = totalRating / businessRatings.length;

    return {
      averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
      reviewCount: businessRatings.length
    };
  };

  // Fetch ratings data (matching HomePage)
  const fetchRatings = async () => {
    try {
      setRatingsLoading(true);
      const response = await fetch(`${baseurl}/api/ratings/all`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch ratings');
      }
      const data = await response.json();
      const ratingsData = Array.isArray(data?.data) ? data.data : [];
      setRatings(ratingsData);
    } catch (err) {
      setRatingsError(err.message || 'Unable to load ratings');
      console.error('Error fetching ratings:', err);
    } finally {
      setRatingsLoading(false);
    }
  };

  // Get current user ID from localStorage (matching HomePage)
  useEffect(() => {
    const memberData = localStorage.getItem('memberData');
    if (memberData) {
      try {
        const parsedData = JSON.parse(memberData);
        setCurrentUserId(parsedData.mid);
      } catch (error) {
        console.error('Error parsing member data:', error);
      }
    }
  }, []);

  // Function to get member name (matching HomePage logic)
  const getMemberName = (business) => {
    const memberFirstName = business?.member?.first_name || business?.Member?.first_name || '';
    const memberLastName = business?.member?.last_name || business?.Member?.last_name || '';
    return [memberFirstName, memberLastName].filter(Boolean).join(' ');
  };

  // Fetch categories and business counts from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        // Fetch categories, businesses, and ratings in parallel
        const [categoriesResponse, businessesResponse] = await Promise.all([
          fetch(`${baseurl}/api/category/all`),
          fetch(`${baseurl}/api/business-profile/all`)
        ]);

        const categoriesData = await categoriesResponse.json();
        const businessesData = await businessesResponse.json();

        // Fetch ratings separately
        await fetchRatings();

        if (categoriesData.success) {
          // Get business profiles - FIXED: Use the correct property from API response
          const profiles = Array.isArray(businessesData?.data)
            ? businessesData.data
            : Array.isArray(businessesData?.profiles)
            ? businessesData.profiles
            : [];

          console.log('Categories data:', categoriesData.data);
          console.log('Business profiles:', profiles);

          // Store businesses for later use
          setBusinesses(profiles);

          // Count businesses per category using both business_type and category_id
          // EXCLUDE CURRENT USER'S BUSINESSES FROM THE COUNT
          const businessCounts = {};
          profiles.forEach(business => {
            // Skip current user's own businesses (same logic as HomePage)
            if (currentUserId && business?.member_id === currentUserId) {
              return;
            }

            // Try to match by category_id first, then fallback to business_type
            const categoryId = business?.category_id;
            const businessType = business?.business_type;

            if (categoryId) {
              // Find the category name by ID
              const category = categoriesData.data.find(cat => cat.cid === categoryId);
              if (category) {
                businessCounts[category.category_name] = (businessCounts[category.category_name] || 0) + 1;
              }
            } else if (businessType) {
              // Fallback to business_type matching
              businessCounts[businessType] = (businessCounts[businessType] || 0) + 1;
            }
          });

          console.log('Business counts (excluding current user):', businessCounts);

          // Transform API data to match UI structure - USING SINGLE GREEN COLOR SCHEME
          const transformedCategories = categoriesData.data.map((category) => {
            const count = businessCounts[category.category_name] || 0;
            return {
              id: category.cid,
              name: category.category_name,
              count: `${count} ${count === 1 ? 'business' : 'businesses'}`,
              businessCount: count,
              ...greenColorScheme // Use the same green color scheme for all
            };
          }).filter(category => {
            // Only show categories that have businesses (excluding current user's)
            const count = parseInt(category.count);
            return count > 0;
          });

          setCategories(transformedCategories);
        } else {
          setError(categoriesData.msg || 'Failed to fetch categories');
        }
      } catch (err) {
        setError('Network error. Please try again.');
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [currentUserId]); // Add currentUserId as dependency

  // Handle category explore button click
  const handleExploreCategory = (category) => {
    setSelectedCategory(category);
    setShowBusinesses(true);
    setCurrentPage(1);
  };

  // Handle back to categories
  const handleBackToCategories = () => {
    setShowBusinesses(false);
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset category page when search term changes
  useEffect(() => {
    setCategoryCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination for categories
  const categoryTotalItems = filteredCategories.length;
  const categoryTotalPages = Math.ceil(categoryTotalItems / categoryItemsPerPage);
  const categoryStartIndex = (categoryCurrentPage - 1) * categoryItemsPerPage;
  const categoryEndIndex = categoryStartIndex + categoryItemsPerPage;
  const currentCategoryItems = filteredCategories.slice(categoryStartIndex, categoryEndIndex);

  // Filter businesses by selected category - EXCLUDE CURRENT USER'S BUSINESSES
  const filteredBusinesses = businesses.filter((business) => {
    // Hide user's own business profile (SAME LOGIC AS HOMEPAGE)
    if (currentUserId && business?.member_id === currentUserId) {
      return false;
    }

    if (!selectedCategory) return false;

    // Try to match by category_id first, then fallback to business_type
    const categoryId = business?.category_id;
    const businessType = business?.business_type;

    if (categoryId) {
      return categoryId === selectedCategory.id;
    } else if (businessType) {
      return businessType === selectedCategory.name;
    }

    return false;
  });

  // Calculate pagination for businesses
  const totalItems = filteredBusinesses.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredBusinesses.slice(startIndex, endIndex);

  const pageBtn = "px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-md border border-line/15 bg-surface text-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:text-ink";
  const pageBtnSm = "px-3 py-2 text-xs rounded-md border border-line/15 bg-surface text-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:text-ink";

  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0">
      <Header />

      {/* Hero Section */}
      <div className="on-dark bg-navy py-6 sm:py-8 lg:py-12">
        <Container className="max-w-7xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-primary/15 ring-1 ring-inset ring-primary/30 rounded-xl sm:rounded-2xl">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
            </div>
            <Eyebrow className="justify-center mb-3">KBC Directory</Eyebrow>
            <Heading variant="display" className="mt-3">
              {showBusinesses ? <>{selectedCategory?.name} <em className="kbc-serif">Businesses</em></> : <>Business <em className="kbc-serif">Categories</em></>}
            </Heading>
            <p className="text-text text-base sm:text-lg">
              {showBusinesses
                ? `Discover ${selectedCategory?.name.toLowerCase()} businesses and services`
                : 'Discover businesses by category and industry'
              }
            </p>
          </div>

          {/* Search Section - Only show when not in business view */}
          {!showBusinesses && (
            <Card className="max-w-4xl mx-auto" padding="md">
              <Input
                type="text"
                placeholder="Search Categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 sm:h-5 sm:w-5" />}
              />
            </Card>
          )}
        </Container>
      </div>

      <Container className="max-w-7xl py-6 sm:py-8">
        {/* Breadcrumb and Back Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="flex items-center text-xs sm:text-sm text-muted">
            <span className="text-muted">Home</span>
            <span className="mx-1 sm:mx-2 text-muted/60">/</span>
            <span className="text-primary">Categories</span>
            {showBusinesses && (
              <>
                <span className="mx-1 sm:mx-2 text-muted/60">/</span>
                <span className="text-ink truncate">{selectedCategory?.name}</span>
              </>
            )}
          </div>

          {/* Back button when showing businesses */}
          {showBusinesses && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCategories}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              className="self-start sm:self-auto"
            >
              Back to Categories
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <Loader label="Loading categories…" />
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-lg border border-danger/20 bg-danger/10 p-8 text-center mb-8">
            <div className="text-danger text-4xl mb-4">⚠️</div>
            <p className="text-danger font-medium text-lg">{error}</p>
            <div className="mt-4 flex justify-center">
              <Button variant="danger" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Business Cards View - UPDATED WITH EXACT HOMEPAGE CARD DIMENSIONS, STYLING, AND RATINGS */}
        {!loading && !error && showBusinesses && (
          <>
            {/* Results count */}
            <div className="mb-6 sm:mb-8">
              <Heading variant="section" className="text-xl sm:text-2xl lg:text-3xl mb-2">
                {totalItems} {totalItems === 1 ? 'Business' : 'Businesses'} Found
              </Heading>
              <p className="text-text text-sm sm:text-base">
                in {selectedCategory?.name}
              </p>
            </div>

            {/* Business Grid - EXACT SAME CARD SIZE AND STYLING AS HOMEPAGE WITH RATINGS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
              {currentItems.map((business) => {
                const imageUrl = getBannerImageUrl(business);

                const title = business?.company_name || 'Business';
                const subtitle = business?.business_type || (business?.Member?.first_name || business?.member?.first_name || '');
                const memberName = getMemberName(business);

                // Get real ratings data (EXACTLY MATCHING HOMEPAGE)
                const { averageRating, reviewCount } = getBusinessRatings(business.id);
                const reviewsCount = reviewCount;
                const ratingValue = averageRating;

                return (
                  <Card key={business.id} interactive padding="none" className="overflow-hidden">
                    <Link to={`/details/${business.id}`}>
                      <div className="relative">
                        {/* Green header section - EXACT SAME HEIGHT AS HOMEPAGE */}
                        <div
                          className="flex h-24 w-full items-center justify-center border-b border-line/10 bg-surface2 bg-cover bg-no-repeat bg-center"
                          style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
                        >
                          {!imageUrl && <Building2 className="h-6 w-6 text-muted" aria-hidden="true" />}
                        </div>

                        {/* Avatar and member name positioned on the right side between green and white sections - EXACT SAME POSITIONING */}
                        <div className="absolute right-5 top-14 flex flex-col items-end z-10">
                          <Avatar
                            size="lg"
                            name={memberName}
                            src={business?.Member?.profile_image ? `${baseurl}/${business.Member.profile_image}` : undefined}
                            className="ring-2 ring-line/20 shadow-card"
                          />
                          {memberName && (
                            <div className="mt-1 text-sm font-medium text-ink bg-surface/80 px-2 py-0.5 rounded text-right">
                              {memberName}
                            </div>
                          )}
                        </div>

                        {/* Content section with left alignment - EXACT SAME PADDING AND SPACING */}
                        <div className="px-3 sm:px-4 pt-10 sm:pt-12 pb-3 text-left">
                          <h3 className="font-display text-lg sm:text-xl font-extrabold text-ink mb-2 text-left">{title}</h3>
                          <p className="text-muted text-sm sm:text-base mb-3 text-left">{subtitle}</p>

                          {/* Ratings Section - EXACT SAME AS HOMEPAGE */}
                          <div className="flex items-center gap-2 sm:gap-3 mb-3 justify-start">
                            <div className="flex items-center text-warning">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-3 h-3 sm:w-4 sm:h-4 ${star <= Math.floor(ratingValue)
                                    ? 'fill-warning text-warning'
                                    : star === Math.ceil(ratingValue) && ratingValue % 1 !== 0
                                      ? 'fill-warning/40 text-warning'
                                      : 'text-muted/40'
                                    }`}
                                />
                              ))}
                            </div>
                            <div className="flex items-baseline gap-1 sm:gap-2">
                              <span className="text-ink text-sm sm:text-lg font-semibold">
                                {ratingValue > 0 ? ratingValue.toFixed(1) : '0.0'}
                              </span>
                              <span className="text-muted text-xs sm:text-sm">
                                ({reviewsCount} {reviewsCount === 1 ? 'review' : 'reviews'})
                              </span>
                            </div>
                          </div>

                          {/* Best time to contact - Added from HomePage */}
                          <div className="text-text mb-3 sm:mb-4 text-left text-xs sm:text-sm">
                            Best time to contact: <span className="font-medium text-ink">{business?.best_contact_time || 'Morning'}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="!rounded-full"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/details/${business.id}`);
                              }}
                            >
                              View
                            </Button>

                            {/* Phone and WhatsApp icons */}
                            <div className="flex items-center gap-2 ml-auto">
                              <a
                                href={`tel:${business.business_work_contract || business.phone || ''}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-info hover:text-info/80 transition-colors"
                                title="Call"
                              >
                                <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                              </a>
                              <a
                                href='#'
                                className="text-primary hover:text-primary-hi transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title="WhatsApp"
                              >
                                <WhatsAppIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </Card>
                );
              })}
            </div>

            {/* No businesses found */}
            {currentItems.length === 0 && (
              <EmptyState
                icon={<Building2 className="w-7 h-7" />}
                title="No businesses found"
                description="No businesses found in this category"
                action={
                  <Button onClick={handleBackToCategories}>
                    Back to Categories
                  </Button>
                }
              />
            )}

            {/* Pagination Controls - EXACT SAME AS HOMEPAGE */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8 mt-8">
                {/* Results info */}
                <div className="text-xs sm:text-sm text-muted">
                  Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} businesses
                </div>

                {/* Pagination buttons */}
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className={pageBtn}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={pageBtn}
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-md border transition-colors ${currentPage === pageNum
                            ? 'bg-primary text-onprimary border-primary'
                            : 'border-line/15 bg-surface text-text hover:bg-primary/10 hover:text-ink'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={pageBtn}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={pageBtn}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}

            {/* Load More Button (alternative to pagination) - EXACT SAME AS HOMEPAGE */}
            {currentPage < totalPages && (
              <div className="text-center mb-8">
                <Button
                  variant="ghost"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Load More Businesses ({totalItems - endIndex} remaining)
                </Button>
              </div>
            )}
          </>
        )}

        {/* Categories Grid View - ALL CARDS NOW HAVE SAME GREEN COLOR */}
        {!loading && !error && !showBusinesses && (
          <>
            {/* Results count */}
            <div className="mb-6 sm:mb-8">
              <Heading variant="section" className="text-xl sm:text-2xl lg:text-3xl">
                {categoryTotalItems} {categoryTotalItems === 1 ? 'Category' : 'Categories'} Found
              </Heading>
              {searchTerm && (
                <p className="text-text text-sm sm:text-base mt-1">
                  Search results for "{searchTerm}"
                </p>
              )}
            </div>

            {/* Enhanced Categories Grid - ALL GREEN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {currentCategoryItems.map((category) => (
                <Card
                  key={category.id}
                  interactive
                  padding="none"
                  className="overflow-hidden"
                >
                  {/* Category header: neutral surface, brand green kept as an
                      accent on the icon rather than a full-bleed fill. */}
                  <div className="relative border-b border-line/10 bg-surface2 p-3">
                    <div>
                      <div className="flex items-center gap-2.5">
                        {/* Category Icon */}
                        <div className="rounded-md bg-primary/10 p-2 text-primary">
                          <Building2 className="w-4 h-4" />
                        </div>

                        {/* Category Name */}
                        <div className="flex-1 min-w-0 text-left">
                          <h3 className="font-display text-base font-semibold text-ink truncate">{category.name}</h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Category Content */}
                  <div className="p-4 sm:p-6">
                    <div className="space-y-4 sm:space-y-6">

                      {/* Business Count */}
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 ring-1 ring-inset ring-primary/20 rounded-xl">
                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="font-display text-2xl sm:text-3xl font-bold text-ink glow-num">{category.businessCount}</p>
                            <p className="text-xs sm:text-sm text-muted">Businesses</p>
                          </div>
                        </div>

                        {/* Explore Button */}
                        <Button
                          onClick={() => handleExploreCategory(category)}
                          className="whitespace-nowrap"
                        >
                          Explore
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* No Results */}
            {currentCategoryItems.length === 0 && searchTerm && (
              <EmptyState
                icon={<Building2 className="w-7 h-7" />}
                title="No categories found"
                description={`No categories found matching "${searchTerm}"`}
                action={
                  <Button onClick={() => setSearchTerm('')}>
                    Clear Search
                  </Button>
                }
              />
            )}

            {/* Pagination Controls for Categories */}
            {categoryTotalPages > 1 && (
              <div className="flex flex-col gap-4 mb-6 sm:mb-8">
                {/* Results info */}
                <div className="text-xs sm:text-sm text-muted text-center sm:text-left">
                  Showing {categoryStartIndex + 1}-{Math.min(categoryEndIndex, categoryTotalItems)} of {categoryTotalItems} categories
                </div>

                {/* Pagination buttons */}
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-1">
                  {/* Mobile: Show only essential buttons */}
                  <div className="flex items-center gap-1 sm:hidden">
                    <button
                      onClick={() => setCategoryCurrentPage(categoryCurrentPage - 1)}
                      disabled={categoryCurrentPage === 1}
                      className={pageBtnSm}
                    >
                      Prev
                    </button>

                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(3, categoryTotalPages) }, (_, i) => {
                        let pageNum;
                        if (categoryTotalPages <= 3) {
                          pageNum = i + 1;
                        } else if (categoryCurrentPage <= 2) {
                          pageNum = i + 1;
                        } else if (categoryCurrentPage >= categoryTotalPages - 1) {
                          pageNum = categoryTotalPages - 2 + i;
                        } else {
                          pageNum = categoryCurrentPage - 1 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCategoryCurrentPage(pageNum)}
                            className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                              categoryCurrentPage === pageNum
                                ? 'bg-primary text-onprimary border-primary'
                                : 'border-line/15 bg-surface text-text hover:bg-primary/10 hover:text-ink'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCategoryCurrentPage(categoryCurrentPage + 1)}
                      disabled={categoryCurrentPage === categoryTotalPages}
                      className={pageBtnSm}
                    >
                      Next
                    </button>
                  </div>

                  {/* Desktop: Show full pagination */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={() => setCategoryCurrentPage(1)}
                      disabled={categoryCurrentPage === 1}
                      className={pageBtn}
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCategoryCurrentPage(categoryCurrentPage - 1)}
                      disabled={categoryCurrentPage === 1}
                      className={pageBtn}
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, categoryTotalPages) }, (_, i) => {
                        let pageNum;
                        if (categoryTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (categoryCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (categoryCurrentPage >= categoryTotalPages - 2) {
                          pageNum = categoryTotalPages - 4 + i;
                        } else {
                          pageNum = categoryCurrentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCategoryCurrentPage(pageNum)}
                            className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                              categoryCurrentPage === pageNum
                                ? 'bg-primary text-onprimary border-primary'
                                : 'border-line/15 bg-surface text-text hover:bg-primary/10 hover:text-ink'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCategoryCurrentPage(categoryCurrentPage + 1)}
                      disabled={categoryCurrentPage === categoryTotalPages}
                      className={pageBtn}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCategoryCurrentPage(categoryTotalPages)}
                      disabled={categoryCurrentPage === categoryTotalPages}
                      className={pageBtn}
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Container>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default BusinessCategories;
